import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronRight, LayoutGrid, Loader2, Repeat } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import type { CheckGridOverviewItem } from "./types";
import { CheckgridEntityLink } from "./CheckgridEntityLink";
import { overviewSignal, recurrenceLabel, satisfiedFraction, satisfiedPercent, signalMeta } from "./utils/display";
import { CheckgridMenu, type CheckgridMenuAction } from "./CheckgridMenu";
import { CheckgridProgressBar } from "./CheckgridProgressMini";
import { CheckgridsRailSkeleton } from "./CheckgridsSkeletons";
import { useScrollLoadMore } from "./hooks/useScrollLoadMore";

type Props = {
  grids: CheckGridOverviewItem[];
  selectedId: number | null;
  onSelectOverview: () => void;
  onSelectGrid: (id: number) => void;
  onAction: (action: CheckgridMenuAction, grid: CheckGridOverviewItem, extras?: { file?: File }) => void;
  filterBar?: ReactNode;
  isFiltered?: boolean;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
};

/** HubRow-style name: tooltip only when truncated. */
function RailName({ name, active }: { name: string; active: boolean }) {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  const [open, setOpen] = useState(false);

  const measure = useCallback(() => {
    const el = nameRef.current;
    if (!el) return;
    const next = el.scrollWidth > el.clientWidth + 1;
    setTruncated(next);
    if (!next) setOpen(false);
  }, []);

  useEffect(() => {
    measure();
    const el = nameRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, name, active]);

  return (
    <Tooltip open={truncated && open} onOpenChange={(next) => setOpen(truncated && next)}>
      <TooltipTrigger asChild>
        <span ref={nameRef} className={cn("min-w-0 flex-1 truncate text-sm", active ? "text-foreground" : "text-foreground/90")}>
          {name}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[16rem] text-xs">
        {name}
      </TooltipContent>
    </Tooltip>
  );
}

export function CheckgridsRail({
  grids,
  selectedId,
  onSelectOverview,
  onSelectGrid,
  onAction,
  filterBar,
  isFiltered,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage
}: Props) {
  const overviewActive = selectedId === null;
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);

  useScrollLoadMore(scrollRoot, { hasNextPage, isFetchingNextPage, fetchNextPage });

  return (
    <aside className="flex w-[280px] shrink-0 flex-col border-r border-border bg-background">
      <div className="relative z-[10] shrink-0 bg-background px-3 pt-4 pb-2">
        <button
          type="button"
          onClick={onSelectOverview}
          aria-current={overviewActive ? "page" : undefined}
          className={cn(
            "group relative flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-left",
            "transition-[background-color,transform] duration-150 ease-out",
            "active:scale-[0.99] motion-reduce:active:scale-100",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            overviewActive ? "bg-primary/10 hover:bg-primary/15" : "bg-muted/70 hover:bg-muted"
          )}
        >
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-background text-foreground/80">
            <LayoutGrid className="size-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1 text-sm text-foreground">Right now</span>
          {!overviewActive && (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:group-hover:translate-x-0" aria-hidden />
          )}
        </button>
      </div>

      {filterBar}

      <nav ref={setScrollRoot} className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pb-3">
        {isLoading ? (
          <CheckgridsRailSkeleton />
        ) : grids.length === 0 ? (
          <p className="px-1 py-8 text-center text-xs text-muted-foreground">{isFiltered ? "No checklists match these filters." : "No checklists yet."}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {grids.map((g) => {
              const active = g.id === selectedId;
              const signal = overviewSignal(g);
              const meta = signalMeta(signal);
              const pct = satisfiedPercent(g);
              const { complete, applicable } = satisfiedFraction(g);
              const recurring = g.recurrence !== "none";
              const needsAttention = signal === "attention";
              const progressLabel = applicable > 0 ? `${complete} of ${applicable} complete` : "No applicable checks";

              return (
                <li key={g.id}>
                  <div className={cn("group relative rounded-lg text-left transition-colors duration-150", active ? "bg-primary/10" : "hover:bg-muted/50")}>
                    <button
                      type="button"
                      onClick={() => onSelectGrid(g.id)}
                      className="block w-full rounded-lg py-3 pr-10 pl-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      aria-current={active ? "page" : undefined}
                    >
                      <div className="flex items-center gap-2.5">
                        <RailName name={g.name} active={active} />
                        {needsAttention && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={cn("size-2 shrink-0 rounded-full", meta.dot)} aria-label="Needs attention" />
                            </TooltipTrigger>
                            <TooltipContent side="right" className="text-xs">
                              Needs attention
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {recurring && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex shrink-0 text-muted-foreground/70" aria-label={recurrenceLabel(g.recurrence)}>
                                <Repeat className="size-3.5" aria-hidden />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="text-xs">
                              {recurrenceLabel(g.recurrence)}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <CheckgridEntityLink grid={g} className="mt-0.5 text-[11.5px] text-muted-foreground" />

                      {applicable > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="h-1 flex-1 cursor-default">
                                <CheckgridProgressBar percent={pct} barClassName={meta.bar} label={progressLabel} className="h-1 w-full" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="text-xs">
                              {progressLabel}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className={cn(
                                  "shrink-0 cursor-default whitespace-nowrap text-right font-mono text-[11px] tabular-nums",
                                  signal === "progress" ? "text-muted-foreground" : meta.text
                                )}
                              >
                                {complete} / {applicable}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="text-xs">
                              {pct}% complete
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                    </button>

                    <div className="absolute top-2 right-1.5 opacity-0 transition-opacity duration-100 group-hover:opacity-100 focus-within:opacity-100">
                      <CheckgridMenu grid={g} onAction={onAction} />
                    </div>
                  </div>
                </li>
              );
            })}
            {isFetchingNextPage ? (
              <li className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Loading more…
              </li>
            ) : null}
          </ul>
        )}
      </nav>
    </aside>
  );
}
